import React, { useCallback, useEffect, useState } from "react";
import {
  Breadcrumbs,
  BreadbrumbItem,
  FooterContainer,
  FooterMessage,
  ErrorMessage,
  SuccessMessage,
  FooterButtons,
} from "../shared/Components";
import {
  Button,
  ColumnLayout,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Select,
  SelectProps
} from "@awsui/components-react";
import { Container } from "@awsui/components-react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  createCampaign,
  CreateCampaignRequest,
  listCampaignPrefilteringEndpoints,
  RESERVED_CAMPAIGN_NAMES,
} from "../../common/api";
import { CampaignPrefilteringEndpoint } from "../../appstore/models";

const CreateJobPanelContainer = styled(Container)`
  margin-bottom: 1.5rem;
`;

export interface Error {
  isError: boolean;
  message: string;
}

const CreateCampaign = () => {
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [apiSuccess, setApiSuccess] = useState<boolean>(false);
  const [apiIsRunning, setApiIsRunning] = useState<boolean>(false);
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignNameError, setCampaignNameError] = useState<Error>({
    isError: false,
    message: "",
  });
  const [
    selectedCampaignPrefilteringEndpoint,
    setSelectedCampaignPrefilteringEndpoint,
  ] = useState<SelectProps.Option>({
    label: "None",
    value: "",
  });
  const [tileSize, setTileSize] = useState<string>("");
  const [tileSizeError, setTileSizeError] = useState<Error>({
    isError: false,
    message: "",
  });
  const [inputS3Prefix, setInputS3Prefix] = useState<string>("");
  const [inputS3PrefixError, setInputS3PrefixError] = useState<Error>({
    isError: false,
    message: "",
  });
  const [outputS3Prefix, setOutputS3Prefix] = useState<string>("");
  const [outputS3PrefixError, setOutputS3PrefixError] = useState<Error>({
    isError: false,
    message: "",
  });
  const [
    camapignPrefilteringEndpointsLoaded,
    setCamapignPrefilteringEndpointsLoaded,
  ] = useState(false);
  const [campaignPrefilteringEndpoints, setCampaignPrefilteringEndpoints] =
    useState<CampaignPrefilteringEndpoint[]>([]);
  const fetchPrefilteringEndpoints = useCallback(async () => {
    const response = await listCampaignPrefilteringEndpoints();
    setCampaignPrefilteringEndpoints(response.campaignPrefilteringEndpoints);
    setCamapignPrefilteringEndpointsLoaded(true);
  }, []);
  useEffect(() => {
    fetchPrefilteringEndpoints();
  }, [fetchPrefilteringEndpoints]);
  const history = useHistory();
  const items: BreadbrumbItem[] = [
    {
      text: "Geospatial Data Labeling",
      href: "/",
    },
    {
      text: "Create Geospatial Data Labeling Campaign",
      href: "/geo-campaigns/new",
    },
  ];

  const onCancel = () => {
    history.push("/geo-campaigns");
  };

  const clearErrors = () => {
    setApiErrors([]);
    setCampaignNameError({
      isError: false,
      message: "",
    });
    setTileSizeError({
      isError: false,
      message: "",
    });
    setInputS3PrefixError({
      isError: false,
      message: "",
    });
    setOutputS3PrefixError({
      isError: false,
      message: "",
    });
  };

  const validateForm = () => {
    let hasError = false;

    if (!campaignName.match(/^\w([\w-]*\w)?$/)) {
      setCampaignNameError({
        isError: true,
        message:
          "This field is required. Must start and end with an alphanumeric character. Can contain dashes and underscores",
      });
      hasError = true;
    }
    if (RESERVED_CAMPAIGN_NAMES.indexOf(campaignName) !== -1) {
      setCampaignNameError({
        isError: true,
        message: `Invalid campaign name. Cannot use reserved word ${RESERVED_CAMPAIGN_NAMES.map(
          (n) => `"${n}"`
        ).join(", ")}`,
      });
      hasError = true;
    }

    if (
      tileSize !== "" &&
      (!tileSize.match(/^\d+$/) ||
        parseInt(tileSize) < 128 ||
        parseInt(tileSize) > 2160)
    ) {
      setTileSizeError({
        isError: true,
        message: "Must be a value between 128 and 2160",
      });
      hasError = true;
    }
    if (!inputS3Prefix.match(/^s3:\/\/[a-z0-9.-]+\/((.*\/)+)?$/)) {
      setInputS3PrefixError({
        isError: true,
        message:
          "This field is required, must start with s3:// and must end with a /",
      });
      hasError = true;
    }
    if (!outputS3Prefix.match(/^s3:\/\/[a-z0-9.-]+\/((.*\/)+)?$/)) {
      setOutputS3PrefixError({
        isError: true,
        message:
          "This field is required, must start with s3:// and must end with a /",
      });
      hasError = true;
    }
    return hasError;
  };

  const onSubmit = async () => {
    clearErrors();
    if (validateForm()) {
      return;
    }
    setApiIsRunning(true);

    const createCampaignRequest: CreateCampaignRequest = {
      job_name: campaignName,
      input_geotiffs_s3_prefix: inputS3Prefix,
      output_campaign_s3_prefix: outputS3Prefix,
    };
    if (tileSize) {
      createCampaignRequest.tile_size = Number(tileSize);
    }
    if (selectedCampaignPrefilteringEndpoint.value) {
      createCampaignRequest.sm_detect_features_endpoint =
        selectedCampaignPrefilteringEndpoint.value;
    }

    const createCampaignResult = await createCampaign(createCampaignRequest);
    setApiIsRunning(false);
    if (!createCampaignResult.success) {
      setApiErrors(createCampaignResult.errors);
      return;
    }
    setApiSuccess(true);

    history.push(`/geo-campaigns/${campaignName}`);
  };

  return (
    <>
      <Breadcrumbs items={items} />
      <ColumnLayout columns={1}>
        <div>
          <Form
            header={
              <Header
                variant="h1"
                description="Start a new geospatial data labeling campaign."
              >
                Create Geospatial Data Labeling Campaign
              </Header>
            }
            actions={
              <FooterContainer>
                {apiErrors ? (
                  <FooterMessage>
                    {apiErrors.map((message, i) => (
                      <ErrorMessage key={i} message={message} />
                    ))}
                  </FooterMessage>
                ) : null}
                {apiSuccess ? (
                  <FooterMessage>
                    <SuccessMessage>Success</SuccessMessage>
                  </FooterMessage>
                ) : null}

                <FooterButtons>
                  <Button
                    variant="link"
                    onClick={onCancel}
                    disabled={apiIsRunning}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={onSubmit}
                    disabled={
                      apiIsRunning || !camapignPrefilteringEndpointsLoaded
                    }
                  >
                    {apiIsRunning
                      ? "Creating Campaign..."
                      : camapignPrefilteringEndpointsLoaded
                      ? "Create Campaign"
                      : "Loading Prefiltering Endpoints..."}
                  </Button>
                </FooterButtons>
              </FooterContainer>
            }
          >
            <CreateJobPanelContainer
              id="job-config-panel"
              header={<Header variant="h2">Campaign settings</Header>}
            >
              <SpaceBetween size="l">
                <FormField label="Campaign Name">
                  <Input
                    invalid={campaignNameError.isError}
                    value={campaignName}
                    ariaRequired={true}
                    placeholder="Describe your labeling campaign"
                    onChange={({ detail: { value } }) => setCampaignName(value)}
                  />
                  {campaignNameError.isError && (
                    <ErrorMessage message={campaignNameError.message} />
                  )}
                </FormField>

                <FormField
                  label={
                    <>
                      Tile Size - <em>Optional</em>
                    </>
                  }
                >
                  <Input
                    invalid={tileSizeError.isError}
                    value={tileSize}
                    ariaRequired={true}
                    placeholder="Default: 512"
                    onChange={({ detail: { value } }) => setTileSize(value)}
                  />
                  {tileSizeError.isError && (
                    <ErrorMessage message={tileSizeError.message} />
                  )}
                </FormField>
              </SpaceBetween>
            </CreateJobPanelContainer>

            <CreateJobPanelContainer
              id="input-config-panel"
              header={<Header variant="h2">Data Configuration</Header>}
            >
              <SpaceBetween size="l">
                <FormField label="Input Data S3 Prefix">
                  <Input
                    invalid={inputS3PrefixError.isError}
                    value={inputS3Prefix}
                    ariaRequired={true}
                    placeholder="S3 prefix directory containing geotiff (.tif) files"
                    onChange={({ detail: { value } }) =>
                      setInputS3Prefix(value)
                    }
                  />
                  {inputS3PrefixError.isError && (
                    <ErrorMessage message={inputS3PrefixError.message} />
                  )}
                </FormField>
                <FormField label="Output Data S3 Prefix">
                  <Input
                    invalid={outputS3PrefixError.isError}
                    value={outputS3Prefix}
                    ariaRequired={true}
                    placeholder="S3 prefix to write results. Must be empty."
                    onChange={({ detail: { value } }) =>
                      setOutputS3Prefix(value)
                    }
                  />
                  {outputS3PrefixError.isError && (
                    <ErrorMessage message={outputS3PrefixError.message} />
                  )}
                </FormField>
              </SpaceBetween>
            </CreateJobPanelContainer>

            <CreateJobPanelContainer
              id="output-config-panel"
              header={
                <Header variant="h2">
                  Prefiltering Model Endpoint Configuration - <em>Optional</em>
                </Header>
              }
            >
              <SpaceBetween size="l">
                <FormField label="Prefiltering Model Endpoint">
                  <Select
                    selectedOption={selectedCampaignPrefilteringEndpoint}
                    onChange={({ detail }) =>
                      setSelectedCampaignPrefilteringEndpoint(
                        detail.selectedOption
                      )
                    }
                    options={[
                      {
                        label: "None",
                        value: "",
                      },
                      {
                        label: "Random",
                        value: "Random",
                      },
                      ...campaignPrefilteringEndpoints.map(
                        (campaignPrefilteringEndpoint) => ({
                          label:
                            campaignPrefilteringEndpoint.CampaignPrefilteringEndpointName,
                          value: campaignPrefilteringEndpoint.EndpointName,
                        })
                      ),
                    ]}
                    selectedAriaLabel="Selected"
                    placeholder="None"
                  />
                </FormField>
              </SpaceBetween>
            </CreateJobPanelContainer>
          </Form>
        </div>
      </ColumnLayout>
    </>
  );
};

export default CreateCampaign;
